/**
 * View component for /evaluations/:evaluationId
 *
 * Displays a single evaluation from the 'byId' map in the evaluation reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, history, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';

// import actions
import * as courseActions from '../../course/courseActions';
import * as evaluationActions from '../evaluationActions';
import * as questionActions from '../../question/questionActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as userEvaluationActions from '../../userEvaluation/userEvaluationActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";

// import module components
import EvaluationLayout from '../components/EvaluationLayout.js.jsx';
import QuestionEditor from '../../question/components/QuestionEditor.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import evaluation css modules
import evaluationStyles from '../evaluationModuleStyles.css';


class EvaluationIntro extends Base {
  constructor(props) {
    super(props);
    this.state = {}
    this._bind(
      '_onVideoComplete'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    if(match.params.courseSlug) {
      // non-agenda route.  useful to have course info
      dispatch(courseActions.fetchSingleBySlug(match.params.courseSlug));
    } else {
      // want to make sure the old course info doesn't persist on agenda routes
      dispatch(courseActions.invalidateSelected())
    }
    dispatch(evaluationActions.fetchSingleIfNeeded(match.params.evaluationId)).then(action1 => {
      // fetch UserEvaluation object
      dispatch(userEvaluationActions.fetchSingleUserEvaluationByEvaluation(action1.item._id)).then(action2 => {
        if(!action2.success) {
          alert("Error finding current User Evaluation");
        } else if(!action2.item) {
          // since these objects are created elsewhere, if it DOESN'T exist we need to redirect the user (if they copied the url from someone else, for example)
          // TODO: this should redirect silently when we go live.
          alert("ATTN: You must first start the Module Evaluation.");
          this.props.history.push(`${match.url.replace('evaluations/' + action1.item._id, 'conclusion')}`)
        } else {
          // this is what we want to happen, so do nothing
        }
      })
    })
    dispatch(questionActions.fetchListIfNeeded('_evaluation', match.params.evaluationId));
    // dispatch(contentModuleActions.fetchSingleBySlug(match.params.contentModuleSlug));
  }

  _onVideoComplete(e) {
    console.log('video done');
    console.log(e);
  }


  render() {
    const {
      courseStore
      , defaultQuestion
      , evaluationStore
      , match
      , questionStore
      , contentModuleStore
    } = this.props;
    /**
     * use the selected.getItem() utility to pull the actual evaluation object from the map
     */
    const selectedEvaluation = evaluationStore.selected.getItem();
    const questionList = questionStore.util.getList('_evaluation', match.params.evaluationId);
    const isEmpty = (
      !selectedEvaluation
      || !selectedEvaluation._id
      || evaluationStore.selected.didInvalidate
      // || courseStore.selected.isFetching
      // || !courseStore.selected.id
      || !questionList
    );

    let introVidSources;
    let sortedQuestions;
    if(!isEmpty) {
      sortedQuestions = filterUtils.sortByOrderWeight(questionList);
    }

    let introHTML = null;
    if(selectedEvaluation && selectedEvaluation.introContent && selectedEvaluation.introContent.blocks && selectedEvaluation.introContent.blocks.length > 0) {
      introHTML = richContentUtils.editorState(selectedEvaluation.introContent);
    }

    let postEvalHTML = null;
    if(selectedEvaluation && selectedEvaluation.postEvalContent && selectedEvaluation.postEvalContent.blocks && selectedEvaluation.postEvalContent.blocks.length > 0) {
      postEvalHTML = richContentUtils.editorState(selectedEvaluation.postEvalContent);
    }

    return (
      <EvaluationLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              {isEmpty ?
                (evaluationStore.selected.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: evaluationStore.selected.isFetching ? 0.5 : 1 }}>
                  <div className="yt-row center-horiz">
                    <div className="yt-col full s_80 m_75 l_60 xl_50">
                      <div style={{marginBottom: '10px', marginTop: '10px'}} className="-buttons">
                        <a href={`${window.agendaUrl}/agenda`} className="yt-btn -exit-btn">
                          <i className="fas fa-arrow-left" style={{marginRight: '10px'}}/>
                          Exit to Agenda
                        </a>
                      </div>
                      <h3> Exam </h3>
                      { selectedEvaluation.introVidPath ?
                        <VideoPlayer
                          isAutoPlay={true}
                          onVideoComplete={this._onVideoComplete}
                          playerId={selectedEvaluation._id + '_intro'}
                          playlist={videoUtils.returnPlaylist(selectedEvaluation.introVidPath, selectedEvaluation.introPosterPath, selectedEvaluation.introTracks)}
                          tracking={{
                            _course: courseStore.selected.id
                            , _evaluation: this.props.match.params.evaluationId
                            , _contentModule: selectedEvaluation._contentModule
                            , description: "user-evaluation"
                          }}
                        />
                        : selectedEvaluation.introPosterPath ?
                        <img src={videoUtils.returnPosterUrl(selectedEvaluation.introPosterPath)} />
                        :
                        null
                      }
                      <div className="content-container">
                        <h1> { selectedEvaluation.title } </h1>
                        { introHTML ? renderHTML(introHTML) : '' }
                        <hr/>
                        <div className="yt-row -buttons">
                          {sortedQuestions.length > 0 ?
                            <Link to={`${match.url}/q/${sortedQuestions[0]._id}`} className="yt-btn -next-btn block large">Go to question 1 of {sortedQuestions.length} <i className="-icon fas fa-arrow-right"/></Link>
                            :
                            null
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </section>
        </div>
      </EvaluationLayout>
    )
  }
}

EvaluationIntro.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */

  return {
    courseStore: store.course
    , defaultQuestion: store.question.defaultItem
    , evaluationStore: store.evaluation
    , questionStore: store.question
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(EvaluationIntro)
);



// WEBPACK FOOTER //
// modules/evaluation/views/EvaluationIntro.js.jsx